import { Button, Collapse, Form, Input, Space, Tag, Typography, message } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getRoadmapRequirements,
  addRoadmapRequirement,
  updateRoadmapRequirement,
  deleteRoadmapRequirement,
  reorderRoadmapRequirements,
  getBundles,
  getQuestionnaires,
} from "../../repository/data.repository";
import {
  RoadmapRequirement,
  RoadmapLevelActionOptions,
  roadmapActionLabel,
} from "../../repository/data.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import { useOrderSwap } from "../../hooks/useOrderSwap";
import ConfirmDeleteButton from "../../controls/ConfirmDeleteButton";
import DraftTag from "../../controls/DraftTag";
import { InputObject } from "../../controls/default";
import DynamicForm from "../../controls/DynamicForm";
import { DynamicControl, DynamicSchema } from "../../controls/DynamicForm.default";
import { QueryOperation } from "../../code/search-query/search-query";

export interface RoadmapRequirementsManagerProps {
  readonly stageUid: string;
  readonly onChanged?: () => void;
}

const bundlesRequest = createSearchRequest(getBundles, "name");
// A roadmap stage requirement can only point at a Roadmap-type questionnaire — the wellbeing
// journey runs on Roadmap questionnaires, not one-off Surveys/Tests. Filter the picker server-side. Only the
// PUBLISHED version is deliverable; publishing a newer one moves the step onto it.
const questionnairesRequest = createScopedFilterRequest(getQuestionnaires, "name", () => [
  ["type", "Roadmap"],
  ["status", "Published"],
]);

// The gated-action picker is a static combobox over the single-select RoadmapLevelAction options.
const actionInputs: InputObject[] = RoadmapLevelActionOptions.map((o) => ({
  display: o.label,
  value: o.value,
}));
const actionRequest = (search: string): Promise<InputObject[]> =>
  Promise.resolve(
    actionInputs.filter((o) =>
      o.display.toLowerCase().includes((search ?? "").toLowerCase()),
    ),
  );

// The chosen action drives which (if any) assignable-entity picker is shown.
function buildEditSchema(actionValue: string | undefined): DynamicSchema {
  const entity = RoadmapLevelActionOptions.find((o) => o.value === actionValue)?.entity;
  const entityControls: DynamicControl[] = [];
  if (entity === "questionnaire") {
    entityControls.push({
      type: "combobox",
      name: "assignableQuestionnaire",
      label: "Questionnaire to answer",
      request: questionnairesRequest,
      dependsOn: ["action"],
      rules: [{ required: true, message: "Pick a questionnaire" }],
    });
  } else if (entity === "bundle") {
    entityControls.push({
      type: "combobox",
      name: "assignableBundle",
      label: "Bundle to distribute",
      request: bundlesRequest,
      dependsOn: ["action"],
      rules: [{ required: true, message: "Pick a bundle" }],
    });
  }
  return {
    groups: [
      {
        controls: [
          {
            type: "textbox",
            name: "name",
            label: "Name",
            rules: [{ required: true, message: "Name is required" }],
          },
          { type: "numberbox", name: "order", label: "Order" },
          {
            type: "togglebox",
            name: "isPublished",
            label: "Published",
            checkedText: "Published",
            unCheckedText: "Draft",
            tooltip: "Draft requirements are hidden from companies and excluded from their journey.",
          },
          { type: "textbox", name: "shortDescription", label: "Short Description" },
          { type: "editorbox", name: "description", label: "Description", rows: 3 },
          { type: "editorbox", name: "stepsDescription", label: "Steps Description", rows: 3 },
        ],
      },
      {
        label: "Gated Action",
        controls: [
          { type: "combobox", name: "action", label: "Action", request: actionRequest },
          ...entityControls,
        ],
      },
    ],
  };
}

function RoadmapRequirementsManager(props: RoadmapRequirementsManagerProps) {
  const [form] = Form.useForm();
  const watchedAction = Form.useWatch("action", form);
  const editSchema = useMemo(
    () => buildEditSchema(watchedAction?.value),
    [watchedAction?.value],
  );
  const [requirements, setRequirements] = useState<RoadmapRequirement[]>([]);
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    return getRoadmapRequirements({
      searchQuery: {
        conditions: [
          { field: "roadmapLevelUid", values: [props.stageUid], operation: QueryOperation.Equal },
        ],
      },
      pageNumber: 0,
      pageSize: 1000,
      sortBy: "order",
      sortDescending: false,
    })
      .then((d) => setRequirements(d.data))
      .catch(() => setRequirements([]));
  }, [props.stageUid]);

  const { move, busy: reordering } = useOrderSwap(requirements, reorderRoadmapRequirements, () =>
    load().then(() => props.onChanged?.()),
  );

  useEffect(() => {
    setActiveKey(undefined);
    load();
  }, [load]);

  // Prefill the shared edit form whenever the expanded requirement changes.
  useEffect(() => {
    const r = requirements.find((x) => x.uid === activeKey);
    if (!r) return;
    form.setFieldsValue({
      name: r.name,
      order: r.order,
      isPublished: r.isPublished,
      shortDescription: r.shortDescription,
      description: r.description,
      stepsDescription: r.stepsDescription,
      action:
        r.availableActions != null
          ? { display: roadmapActionLabel(r.availableActions), value: r.availableActions }
          : undefined,
      assignableBundle: r.assignableBundleUid
        ? { display: r.assignableBundleName, value: r.assignableBundleUid }
        : undefined,
      assignableQuestionnaire: r.assignableQuestionnaireUid
        ? { display: r.assignableQuestionnaireName, value: r.assignableQuestionnaireUid }
        : undefined,
    });
  }, [activeKey, requirements, form]);

  function onAdd(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    addRoadmapRequirement({
      roadmapLevelUid: props.stageUid,
      name: trimmed,
      order: requirements.length + 1,
    })
      .then((created: RoadmapRequirement) => {
        setName("");
        return load().then(() => {
          props.onChanged?.();
          if (created?.uid) setActiveKey(created.uid);
        });
      })
      .catch(() => message.error("Couldn't add the requirement"))
      .finally(() => setBusy(false));
  }

  function onSave(uid: string): void {
    form.validateFields().then(() => {
      setSaving(true);
      const v = form.getFieldsValue();
      const actionValue = v.action?.value ?? null;
      const entity = RoadmapLevelActionOptions.find((o) => o.value === actionValue)?.entity;
      updateRoadmapRequirement({
        uid,
        rowVersion: requirements.find((r) => r.uid === uid)?.rowVersion,
        name: v.name,
        order: Number(v.order ?? 0),
        isPublished: !!v.isPublished,
        shortDescription: v.shortDescription,
        description: v.description,
        stepsDescription: v.stepsDescription,
        availableActions: actionValue,
        // Only persist the entity that the chosen action actually uses.
        assignableQuestionnaireUid:
          entity === "questionnaire" ? v.assignableQuestionnaire?.value ?? null : null,
        assignableBundleUid: entity === "bundle" ? v.assignableBundle?.value ?? null : null,
      })
        .then(() => load())
        .then(() => props.onChanged?.())
        .catch(() => message.error("Couldn't save the requirement"))
        .finally(() => setSaving(false));
    });
  }

  function onDelete(r: RoadmapRequirement): void {
    setBusy(true);
    deleteRoadmapRequirement(r.uid)
      .then(() => load())
      .then(() => props.onChanged?.())
      .catch(() => message.error("Couldn't delete the requirement"))
      .finally(() => setBusy(false));
  }

  return (
    <div className="flex flex-col gap-2">
      <Typography.Text strong>Requirements</Typography.Text>
      <Space.Compact className="w-full">
        <Input
          placeholder="New requirement name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={onAdd}
        />
        <Button type="primary" loading={busy} onClick={onAdd}>
          Add
        </Button>
      </Space.Compact>

      <Collapse
        accordion
        activeKey={activeKey}
        onChange={(k) => setActiveKey(Array.isArray(k) ? k[0] : k)}
        items={requirements.map((r, index) => ({
          key: r.uid,
          label: (
            <Space>
              <Tag color="blue">{r.order}</Tag>
              <DraftTag published={r.isPublished} />
              <Typography.Text>{r.name}</Typography.Text>
            </Space>
          ),
          extra: (
            <Space onClick={(e) => e.stopPropagation()}>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0 || reordering}
                onClick={() => move(index, "up")}
              />
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === requirements.length - 1 || reordering}
                onClick={() => move(index, "down")}
              />
              <ConfirmDeleteButton title="Delete this requirement?" onConfirm={() => onDelete(r)} />
            </Space>
          ),
          children:
            r.uid === activeKey ? (
              <div className="flex flex-col gap-2">
                <DynamicForm className="w-auto" form={form} schema={editSchema} />
                <div className="flex flex-row justify-end">
                  <Button type="primary" loading={saving} onClick={() => onSave(r.uid)}>
                    Save Requirement
                  </Button>
                </div>
              </div>
            ) : (
              <div />
            ),
        }))}
      />
    </div>
  );
}

export default RoadmapRequirementsManager;
